import * as React from 'react'

import { inject, observer } from 'mobx-react'

import { useValidateLocationRestrictionsForTimePeriodsQuery } from '~/client/graph/operations/generated/LocationType.generated'
import { parseDefaultOptions } from '~/client/src/shared/components/ResourcePicker/parseDefaultOptions'
import type { ICreationFieldOptions } from '~/client/src/shared/components/ResourceViewEdit/useResourceFields'
import { IWarningNote } from '~/client/src/shared/components/WarningNote/WarningNote'
import { isTemplateFilter } from '~/client/src/shared/constants/FilterPropertyMaps/ResourceFilterPropMap'
import { isPickedAsSet } from '~/client/src/shared/constants/PermitFieldTypeConstants'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import { useCreatableLocationKinds } from '~/client/src/shared/hooks/useCreatableLocationKinds'
import { useLbsRestrictedIds } from '~/client/src/shared/hooks/useLocationFiltering'
import { useLocationResourceStore } from '~/client/src/shared/hooks/useLocationResourceStore'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import type { ICreatableLocationKinds } from '~/client/src/shared/resources/domain/creatableLocationKinds'
import {
  GENERAL_LEAF_BY_ROOT,
  iconNameFromKind,
  isLocationResourceType,
  LOCATION_KIND_PROPERTY,
} from '~/client/src/shared/resources/domain/locationKind'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import { isLocationOfferedForField } from '~/client/src/shared/utils/isLocationOfferedForField'

import SitePermitCreationFormStore from '../../../../../WorkflowCreationForm.store'
import FormResourcePicker from './FormResourcePicker'
import { collectOverlapConflicts } from './overlapMessages'

const CREATION_FIELD_OPTIONS: ICreationFieldOptions = {
  requiredRootFields: ['parent'],
}

// The icon is seeded with the kind: icon-follows-kind runs on user edits, not on a
// starting value.
export function getCreationRootDefaults(
  defaultOptions: Parameters<typeof parseDefaultOptions>[0],
  isCreatable: ICreatableLocationKinds['isCreatable'],
): Record<string, any> | undefined {
  const kinds = parseDefaultOptions(defaultOptions).filterSelection?.get(
    LOCATION_KIND_PROPERTY,
  )
  if (kinds?.size !== 1) return undefined

  const [root] = kinds
  const kind = GENERAL_LEAF_BY_ROOT[root] ?? root
  if (!isCreatable(kind)) return undefined

  return {
    iconName: iconNameFromKind(kind),
    data: { [LOCATION_KIND_PROPERTY]: kind },
  }
}

interface IProps {
  store: SitePermitCreationFormStore
  projectId?: string
  startCreatingRef?: React.MutableRefObject<(() => void) | null>
  onClose?: () => void
  locationAttributesStore?: LocationAttributesStore
}

const FormLocationResourcePicker: React.FC<IProps> = ({
  store,
  projectId,
  startCreatingRef,
  onClose,
  locationAttributesStore,
}) => {
  const { records, loading, schema } = useLocationResourceStore(projectId)
  const { isCreatable } = useCreatableLocationKinds(projectId)

  const {
    selectedField,
    selectedSubField,
    parentFieldValues,
    selectedParentValueIndex,
    selectedFieldValues,
    editablePermit,
    isAdminOrFormsMaster,
  } = store
  const field = selectedSubField || selectedField
  const isLBSLogicEnabled = !!field?.isLBSLogicEnabled
  const lbsReferenceId = isLBSLogicEnabled
    ? parentFieldValues[selectedParentValueIndex]?.id
    : undefined

  const templateFilters = React.useMemo(
    () => (field?.filterOptions ?? []).filter(isTemplateFilter),
    [field?.filterOptions],
  )

  // Selection filters, LBS narrowing included, target the legacy location models, so
  // they are evaluated there.
  const lbsRestrictedIds = useLbsRestrictedIds(
    isLBSLogicEnabled,
    lbsReferenceId,
    locationAttributesStore,
  )
  const recordPredicate = React.useMemo(() => {
    const allowedIds = new Set(
      field
        ? locationAttributesStore.allLocationsWithoutEquipment
            .filter(location =>
              isLocationOfferedForField(
                field,
                location,
                lbsRestrictedIds,
                editablePermit?.mainFieldsByIdMap,
              ),
            )
            .map(location => location.id)
        : [],
    )
    return (record: IResourceRecord) => allowedIds.has(record.id)
  }, [
    field,
    lbsRestrictedIds,
    editablePermit?.mainFieldsByIdMap,
    locationAttributesStore.allLocationsWithoutEquipment,
  ])

  const isMulti = isPickedAsSet(field, store.selectedTableId)
  const editingIndex = store.selectedFieldIndex ?? 0
  // Keyed by content: an empty field reads as a fresh array on every render.
  const slotKey = `${isMulti}|${editingIndex}|${selectedFieldValues
    .map(value => value?.id ?? '')
    .join(',')}`
  const { currentSlotIds, otherSlotIds } = React.useMemo(() => {
    const current: string[] = []
    const others = new Set<string>()
    selectedFieldValues.forEach((value, index) => {
      if (!value?.id) return
      if (isMulti || index === editingIndex) current.push(String(value.id))
      else others.add(String(value.id))
    })
    return { currentSlotIds: [...new Set(current)], otherSlotIds: others }
  }, [slotKey])

  const legacyLocationsById = locationAttributesStore.hierarchyAllAttributesMap
  const isLbsNarrowed =
    !!lbsReferenceId && !!legacyLocationsById[lbsReferenceId]
  const getLocationType = React.useCallback(
    (id: string) => {
      const pendingType = store.getPendingLocationType(id)
      if (pendingType) return pendingType

      const type = legacyLocationsById[id]?.type
      // Any place, a location in the catch-all bucket included (it reports the collapsed
      // Location type); equipment and integrations share the legacy map and are not offered.
      return isLocationResourceType(type) ? type : null
    },
    [store, legacyLocationsById],
  )

  const candidateIds = React.useMemo(() => records.map(r => r.id), [records])
  const timePeriods = store.locationTimePeriods
  const skip =
    !editablePermit?.startDate ||
    !editablePermit?.endDate ||
    !editablePermit?.typeId ||
    !candidateIds.length ||
    !timePeriods.length
  const { data, loading: restrictionsLoading } =
    useValidateLocationRestrictionsForTimePeriodsQuery({
      variables: {
        timePeriods,
        workflowId: editablePermit?.typeId as string,
        locationIds: candidateIds,
        permitId: editablePermit?.id ?? null,
      },
      skip,
    })
  const isCheckingRestrictions = !skip && restrictionsLoading
  const overlapConflicts = React.useMemo(
    () =>
      collectOverlapConflicts(data?.validateLocationRestrictionsForTimePeriods),
    [data],
  )

  const isControlsReady = store.areResourceControlsReadyForType(
    ResourceTypeId.LOCATIONS,
  )

  // Cached per location: the list asks for every row's note whenever its callbacks
  // change, and twice for each row it draws.
  const getNote = React.useMemo(() => {
    const notesById = new Map<string, IWarningNote | null>()
    return (id: string): IWarningNote | null => {
      if (!notesById.has(id)) {
        notesById.set(id, store.getResourceNote(id, overlapConflicts))
      }
      return notesById.get(id)
    }
    // legacyLocationsById recomputes the notes on live restriction edits.
  }, [store, overlapConflicts, legacyLocationsById])

  const isItemDisabled = React.useCallback(
    (id: string) => {
      if (otherSlotIds.has(id)) return true
      if (isAdminOrFormsMaster) return false
      if (isCheckingRestrictions || !isControlsReady) return true
      return getNote(id)?.tone === 'blocked'
    },
    [
      otherSlotIds,
      isAdminOrFormsMaster,
      isCheckingRestrictions,
      isControlsReady,
      getNote,
    ],
  )

  const creationRootDefaults = React.useMemo(
    () => getCreationRootDefaults(field?.defaultOptions, isCreatable),
    [field?.defaultOptions, isCreatable],
  )

  return (
    <FormResourcePicker
      store={store}
      resourceTypeId={ResourceTypeId.LOCATIONS}
      getLocationType={getLocationType}
      records={records}
      loading={loading}
      projectId={projectId}
      defaultName="New Location"
      startCreatingRef={startCreatingRef}
      onClose={onClose}
      mode={isMulti ? 'multi' : 'single'}
      initialSelectedIds={currentSlotIds}
      filterOptions={templateFilters}
      recordPredicate={recordPredicate}
      isItemDisabled={isItemDisabled}
      getNote={getNote}
      resourceSchema={schema}
      countLabel={Localization.translator.locations}
      creationRootDefaults={creationRootDefaults}
      creationFieldOptions={CREATION_FIELD_OPTIONS}
      // A new location's parent is not held to the LBS narrowing.
      isCreationAllowed={store.canCreatePendingResources && !isLbsNarrowed}
    />
  )
}

export default inject('locationAttributesStore')(
  observer(FormLocationResourcePicker),
)
